import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProjects } from "@/hooks/useProjects";
import { useLanguage } from "@/contexts/LanguageContext";
import { Badge } from "@/components/ui/badge";
import getProjectTranslation from "@/utils/getProjectTranslation";
import { Project } from "@/types";

interface TransactionFilterProps {
  onFilter: (id: string | undefined) => void;
  onFilterInvoice: (invoiceNumber: string | undefined) => void;
  onFilterProject: (projectId: string | undefined) => void;
  onClear: () => void;
  currentFilter: string | undefined;
  currentInvoiceFilter: string | undefined;
  currentProjectFilter: string | undefined;
}

const TransactionFilter = ({
  onFilter,
  onFilterInvoice,
  onFilterProject,
  onClear,
  currentFilter,
  currentInvoiceFilter,
  currentProjectFilter,
}: TransactionFilterProps) => {
  const { t, language } = useLanguage();
  const [transactionId, setTransactionId] = useState(currentFilter || "");
  const [invoiceNumber, setInvoiceNumber] = useState(
    currentInvoiceFilter || "",
  );
  const [projectId, setProjectId] = useState(currentProjectFilter || "all");
  const [projectSearch, setProjectSearch] = useState("");
  const { projects } = useProjects();

  const getProjectLabel = (project: Project) =>
    getProjectTranslation(project.nameTranslations, language) || project.title || "";

  const sortedProjects = useMemo(() => {
    return [...(projects || [])].sort((a, b) =>
      getProjectLabel(a).localeCompare(getProjectLabel(b), undefined, {
        sensitivity: "base",
      }),
    );
  }, [projects]);

  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) {
      return sortedProjects;
    }

    const normalizedSearch = projectSearch.toLowerCase().trim();
    return sortedProjects.filter((project) =>
      getProjectLabel(project).toLowerCase().includes(normalizedSearch),
    );
  }, [projectSearch, sortedProjects]);

  // Update local state if props change (e.g. from URL params)
  useEffect(() => {
    setTransactionId(currentFilter || "");
    setInvoiceNumber(currentInvoiceFilter || "");
    setProjectId(currentProjectFilter || "all");
  }, [currentFilter, currentInvoiceFilter, currentProjectFilter]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilter(transactionId.trim() || undefined);
    onFilterInvoice(invoiceNumber.trim() || undefined);
    onFilterProject(projectId === "all" ? undefined : projectId);
  };

  const handleClearTransactionId = () => {
    setTransactionId("");
    onFilter(undefined);
  };

  const handleClearInvoiceNumber = () => {
    setInvoiceNumber("");
    onFilterInvoice(undefined);
  };

  const handleProjectChange = (value: string) => {
    setProjectId(value);
    onFilterProject(value === "all" ? undefined : value);
  };

  return (
    <div className="space-y-4">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col lg:flex-row gap-6 items-end"
      >
        <div className="space-y-2 flex-1">
          <label className="text-sm font-semibold text-gray-800">
            {t("transactions.project")}
          </label>
          <Select
            value={projectId}
            onValueChange={handleProjectChange}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                setProjectSearch("");
              }
            }}
          >
            <SelectTrigger className="bg-white border border-gray-200 rounded-md h-12">
              <SelectValue placeholder={t("transactions.selectProject")} />
            </SelectTrigger>
            <SelectContent>
              <div className="sticky top-0 z-10 bg-white p-2 border-b">
                <Input
                  type="text"
                  value={projectSearch}
                  placeholder={t("common.search")}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="h-9"
                />
              </div>
              <SelectItem value="all">{t("common.allProjects")}</SelectItem>
              {filteredProjects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {getProjectLabel(project)}
                </SelectItem>
              ))}
              {filteredProjects.length === 0 && (
                <div className="px-2 py-2 text-sm text-gray-500">
                  {t("common.noData")}
                </div>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 flex-1">
          <label className="text-sm font-semibold text-gray-800">
            {t("transactions.transactionId")}
          </label>
          <div className="relative">
            <Input
              type="text"
              placeholder={t("transactions.filterById")}
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              className="bg-white border border-gray-200 rounded-md h-12 pl-3 pr-10"
            />
            {transactionId && (
              <button
                type="button"
                onClick={handleClearTransactionId}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="space-y-2 flex-1">
          <label className="text-sm font-semibold text-gray-800">
            {t("transactions.invoiceNumber")}
          </label>
          <div className="relative">
            <Input
              type="text"
              placeholder={t("transactions.filterByInvoice")}
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className="bg-white border border-gray-200 rounded-md h-12 pl-3 pr-10"
            />
            {invoiceNumber && (
              <button
                type="button"
                onClick={handleClearInvoiceNumber}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <div>
          <Button
            type="submit"
            className="w-full lg:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md h-12 px-6"
          >
            <Search size={18} className="mr-2" />
            {t("common.search")}
          </Button>
        </div>
      </form>

      {/* Active Filters - Updated styling */}
      {(currentFilter || currentInvoiceFilter || currentProjectFilter) && (
        <div className="flex flex-wrap gap-2 pt-2">
          {currentProjectFilter && (
            <Badge className="bg-silvaconsult-primary-100 hover:bg-silvaconsult-primary-200 text-gray-800 flex items-center gap-1 border-none rounded px-[8px] py-[8px]">
              {t("transactions.project")}:{" "}
              {(() => {
                const p = projects?.find((p) => p.id === currentProjectFilter);
                return p
                  ? getProjectTranslation(p.nameTranslations, language) || p.title
                  : currentProjectFilter;
              })()}
              <X
                size={14}
                className="cursor-pointer ml-1"
                onClick={() => onFilterProject(undefined)}
              />
            </Badge>
          )}

          {currentFilter && (
            <Badge className="bg-silvaconsult-primary-100 hover:bg-silvaconsult-primary-200 text-gray-800 px-3 py-1 flex items-center gap-1 border-none rounded">
              {t("transactions.transactionId")}: {currentFilter}
              <X
                size={14}
                className="cursor-pointer ml-1"
                onClick={() => onFilter(undefined)}
              />
            </Badge>
          )}

          {currentInvoiceFilter && (
            <Badge className="bg-silvaconsult-primary-100 hover:bg-silvaconsult-primary-200 text-gray-800 px-3 py-1 flex items-center gap-1 border-none rounded">
              {t("transactions.invoiceNumber")}: {currentInvoiceFilter}
              <X
                size={14}
                className="cursor-pointer ml-1"
                onClick={() => onFilterInvoice(undefined)}
              />
            </Badge>
          )}

          {(currentFilter || currentInvoiceFilter || currentProjectFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="text-blue-700 hover:text-blue-900 hover:bg-blue-50 text-xs px-2 py-1 h-auto"
            >
              {t("common.clearAll")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionFilter;
